"use client";

import { useMemo, useState } from "react";
import { Check } from "lucide-react";

import {
  LOCALE_DISPLAY_NAMES,
  filterLocales,
  type Locale,
} from "@/lib/i18n/config";

export function LocalePickerList({
  locale,
  onSelect,
  searchPlaceholder = "Search languages",
  className = "max-h-[min(24rem,70vh)]",
}: {
  locale: Locale;
  onSelect: (next: Locale) => void;
  searchPlaceholder?: string;
  className?: string;
}) {
  const [query, setQuery] = useState("");
  const options = useMemo(() => filterLocales(query), [query]);

  return (
    <div className={`flex min-w-[16rem] flex-col ${className}`}>
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        className="mb-1 w-full rounded-xl border border-[var(--color-surface-border)] bg-[var(--color-surface-glass)] px-3 py-2 text-sm text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-accent-base)]"
      />
      <div className="overflow-y-auto" role="none">
        {options.map((code) => {
          const item = LOCALE_DISPLAY_NAMES[code];
          const active = code === locale;
          return (
            <button
              key={code}
              type="button"
              role="menuitemradio"
              aria-checked={active}
              onClick={() => onSelect(code)}
              className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-glass)] hover:text-[var(--color-text-primary)]"
            >
              <span className="flex min-w-0 items-center gap-3">
                <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
                  {item.code}
                </span>
                <span className="truncate">{item.nativeName}</span>
              </span>
              {active ? (
                <Check size={14} className="shrink-0 text-[var(--color-accent-base)]" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
